import * as React from 'react';
import { cn } from '../../lib/utils';

/* ────────────────────────────────────────────────────────────────────────────
   v9 status indicators — coloured dots + signal bars.

   StatusDot.status semantics (matching .h-dot in v9):
     - on        emerald-500   (Hearth is awake at home, idle)
     - in-higlo  indigo-500    (Hearth is currently inside a Higlo)
     - waiting   amber-500     (not yet connected / pending setup)
     - sleep     slate-400     (asleep)
     - offline   slate-300     (no recent ping)
   ──────────────────────────────────────────────────────────────────────────── */

const DOT_COLOURS = {
  on: 'bg-emerald-500',
  'in-higlo': 'bg-indigo-500',
  waiting: 'bg-amber-500',
  sleep: 'bg-slate-400',
  offline: 'bg-slate-300',
};

export function StatusDot({ status = 'on', className, ...rest }) {
  return (
    <span
      className={cn(
        'inline-block w-1.5 h-1.5 rounded-full flex-shrink-0',
        DOT_COLOURS[status] || DOT_COLOURS.offline,
        className,
      )}
      {...rest}
    />
  );
}

/* Signal strength bands, strongest first. One table drives both the bars
   icon and the text label so the two can never disagree.
     >  -50 dBm → 4 bars green   "Excellent"
     >  -60 dBm → 3 bars green   "Good"
     >  -70 dBm → 2 bars amber   "Medium"
     ≤  -70 dBm → 1 bar  red     "Weak"
     null       → bars render nothing, label renders "-" */
const SIGNAL_BANDS = [
  { minDbm: -50, bars: 4, colourClass: 'bg-emerald-500', label: 'Excellent' },
  { minDbm: -60, bars: 3, colourClass: 'bg-emerald-500', label: 'Good' },
  { minDbm: -70, bars: 2, colourClass: 'bg-amber-500', label: 'Medium' },
  { minDbm: -Infinity, bars: 1, colourClass: 'bg-red-500', label: 'Weak' },
];

function signalBandFor(rssiDbm) {
  if (rssiDbm == null || Number.isNaN(rssiDbm)) return null;
  return SIGNAL_BANDS.find((band) => rssiDbm > band.minDbm) ?? SIGNAL_BANDS[SIGNAL_BANDS.length - 1];
}

/* SignalBars — 4 ascending bars coloured by RSSI strength (v9 .h-signal sN).
   Renders nothing for an unknown RSSI; callers should hide it when offline. */
export function SignalBars({ rssiDbm, className }) {
  const band = signalBandFor(rssiDbm);
  if (!band) return null;

  const heights = ['h-[4px]', 'h-[7px]', 'h-[10px]', 'h-[13px]'];
  return (
    <span
      className={cn('inline-flex items-end gap-[2px]', className)}
      aria-label={`Wifi signal ${band.bars} of 4`}
    >
      {heights.map((h, i) => (
        <span
          key={i}
          className={cn(
            'w-[3px] rounded-[1px]',
            h,
            i < band.bars ? band.colourClass : 'bg-slate-300',
          )}
        />
      ))}
    </span>
  );
}

/* SignalLabel — plain-English strength word for the same bands as SignalBars.
   Renders "-" for an unknown RSSI. */
export function SignalLabel({ rssiDbm, className }) {
  const band = signalBandFor(rssiDbm);
  return <span className={className}>{band ? band.label : '-'}</span>;
}

/* Status badge styles for ownership. v9 .h-badge-owner / -comanager / -invited / -controller. */
const BADGE_VARIANTS = {
  owner: 'bg-amber-100 text-amber-700 border-amber-200',
  comanager: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  controller: 'bg-green-50 text-green-800 border-green-200',
  invited: 'bg-slate-50 text-slate-600 border-slate-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  /* "Your home" / "Another home" badges on the Hearths list */
  yourHome: 'bg-amber-100 text-amber-700 border-amber-200',
  anotherHome: 'bg-slate-50 text-slate-600 border-slate-200',
};

export function HBadge({ variant = 'invited', className, children, ...rest }) {
  return (
    <span
      className={cn(
        'inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full border whitespace-nowrap',
        BADGE_VARIANTS[variant] || BADGE_VARIANTS.invited,
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

/* Chip with status dot prefix. v9 .h-chip used for showing Hearth names inside a Higlo card. */
export function HChip({ status, children, className, ...rest }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-[11px] px-2 py-[3px] rounded-full bg-slate-100 text-slate-700 border border-slate-200 whitespace-nowrap',
        className,
      )}
      {...rest}
    >
      {status && <StatusDot status={status} />}
      {children}
    </span>
  );
}
